import React, { useState } from 'react'
import Translations from '@helpers/translations'
import { useMediaQuery } from '@helpers/hooks'
import { BREAKPOINT_SIZES } from '@consts/index'
import SizeChartTabs from '../SizeChartTabs'
import Toggle from '@components/ui/Toggle'
import SizeChartTable from './SizeChartTable'
import SizeChartTableInternational from './SizeChartTableInternational'
import SizeChartTablesMobile from './SizeChartTablesMobile'
import styles from './styles.module.scss'

const INCHES_VALUE = 2
const CM_VALUE = 1

const getTabsLabelName = (productCategory: string): string => {
  if (productCategory === 'tops') {
    return 'cut'
  }
  return 'build'
}

const SizeChartNote = ({
  productCategory,
  productType
}: {
  productCategory: string
  productType?: string
}) => {
  if (productCategory === 'trousers' || productCategory === 'denim') {
    return Translations.localise('pages.size-chart.htm.trousers.line-1')
  }

  if (productCategory === 'tops') {
    return Translations.localise('pages.size-chart.htm.tops.line-1')
  }

  if (productCategory === 'shorts') {
    return Translations.localise('pages.size-chart.htm.shorts.line-1')
  }

  if (productCategory === 'accessories' && productType) {
    if (/belts/.test(productType)) {
      return Translations.localise('pages.size-chart.htm.accessories.line-1')
    }
  }

  return null
}

export type InternationalTableRowsProperties = {
  name: string
  values: (string | number)[]
}

type SizeChartTableSelectorProps = {
  tabs: Record<string, string>
  productCategory: string
  productType?: string
  selectedBuildType: string
  setSelectedBuildType: (buildType: string) => void
  sizeChartBuild: string[][]
  sizeChartColumn: string[]
  internationalTableRows: InternationalTableRowsProperties[]
}

const SizeChartTableSelector = ({
  tabs,
  productCategory,
  productType,
  selectedBuildType = '',
  setSelectedBuildType,
  sizeChartBuild = [],
  sizeChartColumn,
  internationalTableRows
}: SizeChartTableSelectorProps) => {
  const [measurementType, setMeasurementType] = useState(INCHES_VALUE)
  const handleMeasurementType = () => {
    const type = measurementType === 1 ? INCHES_VALUE : CM_VALUE
    setMeasurementType(type)
  }
  const isMobileDown = useMediaQuery({
    maxWidth: BREAKPOINT_SIZES.TABLET - 1
  })

  const hasSizeChartTableData = sizeChartBuild.length > 0

  const hasMultipleTabs = Object.keys(tabs).length > 1

  return (
    <section className={styles.sizeChart__section}>
      {isMobileDown ? (
        <>
          {hasMultipleTabs && (
            <>
              <h3 className={styles.sizeChart__mobile__title}>
                {Translations.localise(
                  `pages.size-chart.label-${getTabsLabelName(productCategory)}`
                )}
              </h3>
              <SizeChartTabs
                tabs={tabs}
                setSelectedTab={setSelectedBuildType}
                selectedTab={selectedBuildType}
              />
            </>
          )}

          <SizeChartTablesMobile
            sizeChartBuild={sizeChartBuild}
            sizeChartColumn={sizeChartColumn}
            measurementType={measurementType}
            productCategory={productCategory}
            internationalTableRows={internationalTableRows}
            hasSizeChartTableData={hasSizeChartTableData}
          />

          {hasSizeChartTableData ? (
            <div className={styles.sizeChart__toggleButtonWrapper}>
              <Toggle
                toggled={measurementType === 1}
                labelA="Inches"
                labelB="CM"
                labelDir="right"
                onToggle={handleMeasurementType}
              />
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className={styles.sizeChart__titleContainer}>
            <h3 className={styles.sizeChart__subtitle}>
              {Translations.localise(
                'pages.size-chart.label-international-measurements'
              )}
            </h3>
          </div>

          <SizeChartTableInternational rows={internationalTableRows} />

          {hasSizeChartTableData ? (
            <>
              <div className={styles.sizeChart__titleContainer}>
                <h3 className={styles.sizeChart__subtitle}>
                  {Translations.localise(
                    'pages.size-chart.label-product-measurements'
                  )}
                </h3>
                <div className={styles.sizeChart__toggle}>
                  <Toggle
                    toggled={measurementType === 1}
                    labelA="Inches"
                    labelB="Cm"
                    labelDir="left"
                    onToggle={handleMeasurementType}
                  />
                </div>
              </div>

              <p className={styles.sizeChart__note}>
                <SizeChartNote
                  productCategory={productCategory}
                  productType={productType}
                />
              </p>

              {hasMultipleTabs && (
                <SizeChartTabs
                  tabs={tabs}
                  setSelectedTab={setSelectedBuildType}
                  selectedTab={selectedBuildType}
                />
              )}

              <SizeChartTable
                sizeChartBuild={sizeChartBuild}
                sizeChartColumn={sizeChartColumn}
                measurementType={measurementType}
              />
            </>
          ) : null}
        </>
      )}
    </section>
  )
}

export default SizeChartTableSelector
